import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faToggleOff,
  faToggleOn,
  faTrash,
  faPlus,
  faFileImport,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import AdminTableHeader from '../../../components/Admin/AdminTableHeader';
import AdminActionButton from '../../../components/Admin/AdminActionButton';
import AdminSearchInput from '../../../components/Admin/AdminSearchInput';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import Dialogs from '../../../components/Common/Dialog';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import PaginationButtons from '../../../components/Common/Pagination';
import ImportDialog from '../../../components/Common/ImportDialog';
import { Toast } from '../../../components/Common/Toast';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import DistrictForm from './DistrictForm';
import {
  DistrictModel,
  getAdminDistricts,
  saveDistrict,
  updateDistrict,
  deleteDistrict,
  changeDistrictStatus,
  importDistrictsCsv,
  deleteAllDistricts,
} from './services/District.service';
import type { ImportProgressData } from '../../../utils/sseUpload';

const IS_LOCAL = process.env.REACT_APP_ENV === 'localhost' || process.env.REACT_APP_ENV === 'local';

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'nces', label: 'NCES' },
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
  { value: 'clever', label: 'Clever' },
];

const formatDate = (d: any): string => {
  if (!d) return '-';
  const date = typeof d === 'string' ? new Date(d) : d._seconds ? new Date(d._seconds * 1000) : new Date(d);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Districts = () => {
  const [districts, setDistricts] = useState<DistrictModel[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState<Partial<DistrictModel> | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<'status' | 'delete'>('status');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  });

  const loadDistricts = useCallback(
    (page?: number, state?: string, search?: string, source?: string) => {
      setLoading(true);
      getAdminDistricts(
        (data) => {
          setDistricts(data);
          setLoading(false);
        },
        (pages) => setTotalPages(pages),
        page || currentPage,
        state ?? (stateFilter || undefined),
        search ?? (searchText || undefined),
        source ?? (sourceFilter || undefined),
        (tr) => setTotalRecords(tr),
      );
    },
    [currentPage, stateFilter, searchText, sourceFilter]
  );

  useEffect(() => {
    loadDistricts(currentPage, stateFilter || undefined, searchText || undefined, sourceFilter || undefined);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced auto-apply when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadDistricts(1, stateFilter || undefined, searchText || undefined, sourceFilter || undefined);
    }, 400);
    return () => clearTimeout(timer);
  }, [stateFilter, sourceFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    loadDistricts(1, stateFilter || undefined, value || undefined, sourceFilter || undefined);
  };

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages) setCurrentPage(+page);
  };

  const handleCreate = () => {
    setEditingDistrict(null);
    setShowForm(true);
  };

  const handleEdit = (district: DistrictModel) => {
    setEditingDistrict({ ...district });
    setShowForm(true);
  };

  const reload = () =>
    loadDistricts(currentPage, stateFilter || undefined, searchText || undefined, sourceFilter || undefined);

  const handleSave = async (data: Partial<DistrictModel>) => {
    if (data.id) {
      await updateDistrict(data as Partial<DistrictModel> & { id: string }, () => {
        setShowForm(false);
        reload();
      });
    } else {
      await saveDistrict(data, () => {
        setShowForm(false);
        reload();
      });
    }
  };

  const handleToggleStatus = (id: string) => {
    setSelectedId(id);
    setDialogAction('status');
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    setSelectedId(id);
    setDialogAction('delete');
    setOpenDialog(true);
  };

  const handleDialogConfirm = async () => {
    if (!selectedId) return;
    setOpenDialog(false);

    if (dialogAction === 'status') {
      await changeDistrictStatus(selectedId, reload);
    } else {
      await deleteDistrict(selectedId, reload);
    }
    setSelectedId(null);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedId(null);
  };

  const handleImportFile = async (file: File, onProgress?: (data: ImportProgressData) => void) => {
    return importDistrictsCsv(file, onProgress);
  };

  const handleClearAll = async () => {
    setClearAllDialogOpen(false);
    setClearingAll(true);
    const result = await deleteAllDistricts();
    setClearingAll(false);
    if (result.success) {
      Toast(result.message);
      reload();
    } else {
      Toast(result.message, true);
    }
  };

  const rowOffset = (currentPage - 1) * 25;

  return (
    <div className="p-0 md:p-4">
      {showForm ? (
        <DistrictForm
          setShowForm={setShowForm}
          districtDetails={editingDistrict}
          onSave={handleSave}
        />
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex justify-between items-center flex-wrap gap-2 my-5">
            <div className="flex items-center gap-2">
              <AdminSearchInput
                placeholder="Search name, city, zip..."
                onSearch={handleSearchChange}
                className="w-64"
              />
              <input
                type="text"
                placeholder="State"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
                className="border border-gray-300 rounded-md px-2 py-1.5 w-20 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                maxLength={2}
              />
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              {IS_LOCAL && (
                <button
                  onClick={() => setClearAllDialogOpen(true)}
                  disabled={clearingAll}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-md hover:shadow-md transition-shadow text-sm disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  {clearingAll ? 'Clearing...' : 'Clear All'}
                </button>
              )}
              <button
                onClick={() => setImportDialogOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:shadow-md transition-shadow"
              >
                <FontAwesomeIcon icon={faFileImport} />
                Import CSV
              </button>
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 bg-storyblue text-white rounded-md hover:shadow-md transition-shadow"
              >
                <FontAwesomeIcon icon={faPlus} />
                New District
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="w-full overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <AdminTableHeader
                    columns={[
                      { key: 'sn', label: 'S#' },
                      { key: 'name', label: 'Name' },
                      { key: 'state', label: 'State' },
                      { key: 'city', label: 'City' },
                      { key: 'leaid', label: 'LEAID' },
                      { key: 'source', label: 'Source' },
                      { key: 'updated', label: 'Updated' },
                      { key: 'status', label: 'Status' },
                      { key: 'actions', label: 'Actions' },
                    ]}
                  />
                  <tbody>
                    {districts && districts.length > 0 ? (
                      districts.map((district, index) => (
                        <tr key={district.id} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                          <td className="text-left py-2 px-4 border-b">{rowOffset + index + 1}</td>
                          <td className="text-left py-2 px-4 border-b">{district.name}</td>
                          <td className="text-left py-2 px-4 border-b">{district.state}</td>
                          <td className="text-left py-2 px-4 border-b">{district.city || '-'}</td>
                          <td className="text-left py-2 px-4 border-b font-mono text-sm">
                            {district.externalId || '-'}
                          </td>
                          <td className="text-left py-2 px-4 border-b">
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              {district.source}
                            </span>
                          </td>
                          <td className="text-left py-2 px-4 border-b text-xs text-gray-500">
                            {formatDate(district.updatedAt)}
                          </td>
                          <td className="text-left py-2 px-4 border-b">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                district.isActive
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {district.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-left py-4 px-4 border-b">
                            <div className="flex gap-3">
                              <AdminActionButton
                                icon={faPenToSquare}
                                title="Edit"
                                hoverColor="hover:text-blue-600"
                                onClick={() => handleEdit(district)}
                              />
                              <AdminActionButton
                                icon={district.isActive ? faToggleOn : faToggleOff}
                                title={district.isActive ? 'Deactivate' : 'Activate'}
                                className={district.isActive ? 'text-storygreen' : 'text-red-500'}
                                hoverColor={district.isActive ? 'hover:text-green-700' : 'hover:text-red-700'}
                                onClick={() => handleToggleStatus(district.id)}
                              />
                              <AdminActionButton
                                icon={faTrash}
                                title="Delete"
                                hoverColor="hover:text-red-600"
                                onClick={() => handleDelete(district.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <NoRecordFound colSpan={9} />
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center mt-4">
                <TableInfoBar
                  currentPage={currentPage}
                  pageSize={25}
                  totalPages={totalPages}
                  totalRecords={totalRecords}
                  recordsOnPage={districts.length}
                />
                {totalPages > 1 && (
                  <PaginationButtons
                    totalPages={totalPages}
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                  />
                )}
              </div>
            </>
          )}

          <Dialogs
            openDialog={openDialog}
            handelClose={handleDialogClose}
            confirmMessage={
              dialogAction === 'status'
                ? 'Are you sure you want to change the district status?'
                : 'Are you sure you want to delete this district?'
            }
            handelProcess={handleDialogConfirm}
          />

          <ImportDialog
            open={importDialogOpen}
            title="Import Districts CSV"
            onClose={() => setImportDialogOpen(false)}
            onImport={handleImportFile}
            onComplete={reload}
          />

          {IS_LOCAL && (
            <Dialogs
              openDialog={clearAllDialogOpen}
              handelClose={() => setClearAllDialogOpen(false)}
              confirmMessage="Are you sure you want to DELETE ALL districts? This cannot be undone."
              handelProcess={handleClearAll}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Districts;
